// src/App.js
import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, matchPath } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ROProvider } from "./context/ROContext";
import ServiceDriveCalculatorPage from "./pages/ServiceDriveCalculatorPage";

import ProtectedRoute from "./components/ProtectedRoute";
import AppLayout from "./layout/AppLayout";
import { useAuth } from "./context/AuthContext";
import { RealtimeProvider } from "./context/RealtimeContext";
import { ROMessagesProvider } from "./context/ROMessagesContext";
import { ROUnreadProvider } from "./context/ROUnreadContext";
import { LegalAcceptanceProvider } from "./context/LegalAcceptanceContext";
import LegalAcceptanceGate from "./components/legal/LegalAcceptanceGate";

import MarketingHomePage from "./pages/MarketingHomePage";
import ContactPage from "./pages/ContactPage";

import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import UnauthorizedPage from "./pages/UnauthorizedPage";

import SelectStorePage from "./pages/SelectStorePage";

import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import DpaPage from "./pages/DpaPage";
import AupPage from "./pages/AupPage";
import SecurityPage from "./pages/SecurityPage";
import SubprocessorsPage from "./pages/SubprocessorsPage";

import PlatformAdminHome from "./pages/PlatformAdminHome";
import PlatformBilling from "./pages/PlatformBilling";
import PlatformImpersonate from "./pages/PlatformImpersonate";
import PlatformBusinessUnits from "./pages/PlatformBusinessUnits";
import PlatformUsersHub from "./pages/PlatformUsersHub";
import PlatformBillingSuccess from "./pages/PlatformBillingSuccess";
import PlatformBillingCanceled from "./pages/PlatformBillingCanceled";

import Dashboard from "./pages/Dashboard";
import DispatchPage from "./pages/DispatchPage";
import ROEntryPage from "./pages/ROEntryPage";
import AdvisorDashboard from "./pages/AdvisorDashboard";
import AdvisorLog from "./pages/AdvisorLog";
import AdvisorDeferredServices from "./pages/AdvisorDeferredServices";
import PartsManagerDashboard from "./pages/PartsManagerDashboard";
import ShopForemanDashboard from "./pages/ShopForemanDashboard";
import TechWorkConsole from "./pages/TechWorkConsole";
import TechStats from "./pages/TechStats";
import EditRepairOrdersPage from "./pages/EditRepairOrdersPage";
import ROHistory from "./pages/ROHistory";
import ROSearchPage from "./pages/ROSearchPage";
import ShopScreen from "./pages/ShopScreen";
import StaffPage from "./pages/StaffPage";
import PartsPage from "./pages/PartsPage";
import SettingsPage from "./pages/SettingsPage";

const TrainingRoutes = lazy(() => import("./training/TrainingRoutes"));

function normalizeRole(v) {
  const r = String(v || "").trim().toLowerCase();
  if (r === "tech") return "technician";
  if (r === "service_manager") return "manager";
  if (r === "store_manager") return "manager";
  return r;
}

export function getStoreLandingPath(role) {
  const r = normalizeRole(role);

  if (r === "advisor") return "/advisor-log";
  if (r === "parts") return "/parts-manager";
  if (r === "staff") return "/parts";
  if (r === "foreman") return "/shop-foreman";
  if (r === "technician") return "/tech";

  return "/dashboard";
}

function HomeRedirect() {
  const auth = useAuth();
  const isAuthenticated = !!auth?.isAuthenticated;
  const hydrated = !!auth?.hydrated;
  const mode = auth?.mode === "platform" ? "platform" : "store";
  const isPlatformUser = !!auth?.isPlatformUser;
  const role = auth?.membership?.role || auth?.user?.role || "";

  if (!hydrated) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (mode === "platform" && isPlatformUser) {
    return <Navigate to="/platform/admin" replace />;
  }

  return <Navigate to={getStoreLandingPath(role)} replace />;
}

function AuthenticatedLayout() {
  return (
    <LegalAcceptanceProvider>
      <LegalAcceptanceGate>
        <RealtimeProvider>
          <ROUnreadProvider>
            <ROMessagesProvider>
              <AppLayout />
            </ROMessagesProvider>
          </ROUnreadProvider>
        </RealtimeProvider>
      </LegalAcceptanceGate>
    </LegalAcceptanceProvider>
  );
}

// The calculator and Training must not initialize operational state or persistence.
// Keep the existing provider hierarchy for every other route.
export function RouteProviders({ children }) {
  const { pathname } = useLocation();
  if (matchPath({ path: "/calculator", end: true }, pathname) || matchPath("/training/*", pathname)) return children;
  return <AuthProvider><ROProvider>{children}</ROProvider></AuthProvider>;
}

export default function App() {
  return (
    <BrowserRouter>
      <RouteProviders>
      <Routes>
        <Route path="/training/*" element={<Suspense fallback={<div role="status">Loading Training preview…</div>}><TrainingRoutes /></Suspense>} />
        {/* Public marketing / sales pages */}
        <Route path="/" element={<MarketingHomePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/calculator" element={<ServiceDriveCalculatorPage />} />

        {/* Public auth/legal pages */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        <Route
          path="/platform/login"
          element={<Navigate to="/login" replace />}
        />

        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/dpa" element={<DpaPage />} />
        <Route path="/aup" element={<AupPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/subprocessors" element={<SubprocessorsPage />} />

        {/* Authenticated app landing redirect */}
        <Route path="/app" element={<HomeRedirect />} />

        <Route element={<AuthenticatedLayout />}>
          <Route element={<ProtectedRoute requirePlatform />}>
            <Route
              path="/platform"
              element={<Navigate to="/platform/admin" replace />}
            />
            <Route
              path="/platform/admin-home"
              element={<Navigate to="/platform/admin" replace />}
            />
            <Route path="/platform/admin" element={<PlatformAdminHome />} />
            <Route path="/platform/users" element={<PlatformUsersHub />} />
            <Route
              path="/platform/app-users"
              element={<Navigate to="/platform/users?tab=app" replace />}
            />
            <Route
              path="/platform/business-units"
              element={<PlatformBusinessUnits />}
            />
            <Route
              path="/platform/onboard"
              element={<Navigate to="/platform/business-units" replace />}
            />
            <Route
              path="/platform/stores"
              element={<Navigate to="/platform/business-units" replace />}
            />

            <Route
              element={
                <ProtectedRoute
                  requirePlatform
                  allowedRoles={["SUPER_ADMIN"]}
                />
              }
            >
              <Route path="/platform/billing" element={<PlatformBilling />} />
              <Route
                path="/platform/billing/success"
                element={<PlatformBillingSuccess />}
              />
              <Route
                path="/platform/billing/canceled"
                element={<PlatformBillingCanceled />}
              />
            </Route>

            <Route
              path="/platform/impersonate"
              element={<PlatformImpersonate />}
            />
          </Route>

          <Route element={<ProtectedRoute />}>
            <Route path="/select-store" element={<SelectStorePage />} />
            <Route path="/settings" element={<SettingsPage />} />

            <Route
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "manager",
                    "parts",
                    "foreman",
                    "dispatcher",
                    "admin",
                  ]}
                />
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/shop-screen" element={<ShopScreen />} />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  allowedRoles={["manager", "parts", "dispatcher", "admin"]}
                />
              }
            >
              <Route path="/parts-manager" element={<PartsManagerDashboard />} />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  allowedRoles={["manager", "foreman", "dispatcher", "admin"]}
                />
              }
            >
              <Route path="/shop-foreman" element={<ShopForemanDashboard />} />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "manager",
                    "advisor",
                    "technician",
                    "dispatcher",
                    "foreman",
                    "parts",
                    "staff",
                    "admin",
                  ]}
                />
              }
            >
              <Route path="/dispatch" element={<DispatchPage />} />
              <Route path="/ro-search" element={<ROSearchPage />} />
              <Route path="/ro-history" element={<ROHistory />} />
            </Route>

            <Route
              element={<ProtectedRoute allowedRoles={["manager", "advisor"]} />}
            >
              <Route path="/ro-entry" element={<ROEntryPage />} />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  allowedRoles={["manager", "advisor", "admin"]}
                />
              }
            >
              <Route path="/advisor" element={<AdvisorDashboard />} />
              <Route path="/advisor-log" element={<AdvisorLog />} />
              <Route
                path="/edit-repair-orders"
                element={<EditRepairOrdersPage />}
              />
            </Route>

            <Route element={<ProtectedRoute allowedRoles={["advisor"]} />}>
              <Route
                path="/advisor-deferred"
                element={<AdvisorDeferredServices />}
              />
            </Route>

            <Route
              element={<ProtectedRoute allowedRoles={["parts", "staff"]} />}
            >
              <Route path="/parts" element={<PartsPage />} />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "manager",
                    "dispatcher",
                    "technician",
                  ]}
                />
              }
            >
              <Route path="/tech" element={<TechWorkConsole />} />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "manager",
                    "foreman",
                    "dispatcher",
                    "technician",
                    "admin",
                  ]}
                />
              }
            >
              <Route path="/tech-stats" element={<TechStats />} />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  allowedRoles={["manager", "parts", "foreman", "admin"]}
                />
              }
            >
              <Route path="/staff" element={<StaffPage />} />
            </Route>

          </Route>
        </Route>

        <Route path="*" element={<HomeRedirect />} />
      </Routes>
      </RouteProviders>
    </BrowserRouter>
  );
}
